import { IntlShape } from 'react-intl';
import { formatCurrencyPosition } from 'utils/bankPageUtils';
import {
  Deposit,
  DepositInsuranceDescriptionID,
  DepositInsuranceDescriptionType,
  InterestBookingInterval,
  InterestPayoutScheme,
} from '../../types';

export const enum PRODUCT_TYPES {
  OVERNIGHT = 'OVERNIGHT',
  NOTICE = 'NOTICE',
  TERM = 'TERM',
}

export const isOvernight = (productType: string, marketedAs?: string): boolean =>
  marketedAs ? marketedAs === PRODUCT_TYPES.OVERNIGHT : productType === PRODUCT_TYPES.OVERNIGHT;
export const isNotice = (productType: string, marketedAs?: string): boolean =>
  marketedAs ? marketedAs === PRODUCT_TYPES.NOTICE : productType === PRODUCT_TYPES.NOTICE;
export const isTerm = (productType: string, marketedAs?: string): boolean =>
  marketedAs ? marketedAs === PRODUCT_TYPES.TERM : productType === PRODUCT_TYPES.TERM;

const isFlexgeld = (product) =>
  product.product_type === PRODUCT_TYPES.TERM &&
  product?.conditions?.termination?.contractual_termination_allowed === true;

const hasAutoProlongationEnabled = (productConditions) =>
  productConditions?.renewal?.enabled && productConditions?.renewal?.default_product !== null;

const getInterestPayoutScheme = (annuityConditions): InterestPayoutScheme => {
  if (annuityConditions?.enabled && annuityConditions?.annuity_payout)
    return InterestPayoutScheme.Annual;
  return InterestPayoutScheme.Maturity;
};

const getBookingInterval = (annuityConditions, product_type): InterestBookingInterval => {
  if (!annuityConditions?.enabled && product_type === PRODUCT_TYPES.TERM)
    return InterestBookingInterval.Maturity;

  if (!annuityConditions.frequency) return InterestBookingInterval.Empty;

  const {
    frequency: { unit, period },
  } = annuityConditions;

  if (unit === 'months' && period === 3) return InterestBookingInterval.Quarterly;
  else if (unit === 'months' && period === 1) return InterestBookingInterval.Monthly;
  else if (unit === 'months' && period === 12) return InterestBookingInterval.Annually;
  return InterestBookingInterval.Empty;
};

const applicationDocumentMap = {
  not_applicable: 'NONE',
  digital: 'DIGIT',
  physical: 'PAPER',
};

const DISPLAY_LABELS = {
  TOP_OFFER: 'top_offer',
  SPECIAL_OFFER: 'special_offer',
  RAISIN_EXCLUSIVE: 'raisin_exclusive',
  CHANNEL_EXCLUSIVE: 'channel_exclusive',
  MARKET_LEADING_RATE: 'market_leading_rate',
  STRUCTURED_DEPOSIT: 'structured_deposit',
  RAISIN_BANK_PROMOTIONAL_FLAG: 'raisin_bank_promotional_offer',
};

const isLabelAvailable = (productDisplay, currentLabel) => {
  const { labels } = productDisplay;
  return labels?.includes(currentLabel);
};

export const getDepositInsuranceValue = (
  statutoryDepositData,
  formatNumber: IntlShape['formatNumber'],
  locale: string,
): string => {
  const { insured_amount: insuredAmount, currency } = statutoryDepositData;
  return formatCurrencyPosition(
    formatNumber(+insuredAmount, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }),
    locale,
  );
};

const getDescriptionDocumentURL = (preContractualDocuments): string =>
  preContractualDocuments?.find((doc) => doc?.document_type === 'PRODUCT_INFORMATION_SHEET')?.url;

export const mapBipsToObsDeposits = (
  product,
  formatNumber: IntlShape['formatNumber'],
  t,
  formatDate: IntlShape['formatDate'],
  locale: IntlShape['locale'],
): Deposit => {
  const { conditions } = product;

  return {
    id: product.id,
    settlement_day: product.conditions.settlement_day,
    is_rollover_enabled: product.conditions?.rollover?.enabled,
    daily_settlement: product.conditions.daily_settlement,
    has_fixed_investment_amount: conditions?.top_up?.enabled,
    restricted_to_new_customers:
      conditions?.opening?.restricted_to_new_customers === 'restricted_to_new_customers',
    is_in_foreign_currency: false,
    min_placement_amount: Number(conditions?.opening?.minimum_amount),
    max_placement_amount: Number(conditions?.maximum_balance),
    partner_bank: {
      id: product?.deposit_taking_bank?.id,
    },
    is_overnight: isOvernight(product?.product_type, product?.marketed_as_product_type),
    is_notice_account: isNotice(product?.product_type, product?.marketed_as_product_type),
    is_term_account: isTerm(product?.product_type, product?.marketed_as_product_type),
    is_marketed_as_overnight: product?.marketed_as_product_type === PRODUCT_TYPES.OVERNIGHT,
    product_type: product?.product_type,
    marketed_as_product_type: product?.marketed_as_product_type,
    currency: product?.currency,
    nominal_interest_rate: Number(product?.interest_rates?.interest_rate_nominal),
    effective_interest_rate: Number(product?.interest_rates?.interest_rate_effective),
    has_fully_online_process:
      conditions?.opening?.application_document === 'not_applicable' ? true : false,
    is_flexgeld: isFlexgeld(product),
    is_top_offer: isLabelAvailable(product?.display, DISPLAY_LABELS.TOP_OFFER),
    has_auto_prolongation: hasAutoProlongationEnabled(conditions),
    number_of_terms: product?.term?.period, // Note: Only support terms in months.
    payout_period_after_termination: conditions?.termination?.notice_period?.period,
    interest_payout_scheme: getInterestPayoutScheme(conditions?.annuity),
    early_termination_interest_rate: 0.0,
    early_termination_effective_interest_rate: 0.0,
    opening_document: applicationDocumentMap[conditions?.opening?.application_document],
    prolongation_enabled: conditions?.renewal?.enabled,
    source_tax_applicable: product?.deposit_taking_bank?.branch_country?.source_tax_applicable,
    is_sustainable_deposit: product?.display?.labels?.includes('sustainable_deposit'),
    is_contractual_termination_allowed: conditions?.termination?.contractual_termination_allowed,
    termination_interest_policy: conditions?.termination?.interest_policy,
    source_tax_type: product?.deposit_taking_bank?.branch_country?.source_tax_type,
    domestic_taxation: product?.deposit_taking_bank?.branch_country?.domestic_taxation_applicable
      ? product?.deposit_taking_bank?.branch_country?.domestic_taxation_applicable
      : product?.deposit_taking_bank?.branch_country?.domestic_taxation,
    cms_content: {
      deposit_insurance_description: {
        value: product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.description,
      },
      description_document: {
        url_path: getDescriptionDocumentURL(product?.pre_contractual_documents),
      },
      deposit_insurance_name: {
        value: getDepositInsuranceValue(
          product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme,
          formatNumber,
          locale,
        ),
      },
      deposit_insurance_logo: {
        value: product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme.logo_url,
      },
      ...(product?.display?.labels?.includes('finanztest_bestenliste')
        ? {
            finanz_test_label: {
              id: DepositInsuranceDescriptionID.FinanzTestLabel,
              type: DepositInsuranceDescriptionType.StringHTML,
              value: 'Finanztest Bestenliste',
            },
          }
        : {}),
    },
    interest_compounding: conditions?.annuity?.compound_interest,
    interest_booking_interval: getBookingInterval(conditions?.annuity, product?.product_type),
    raisin_exclusive: isLabelAvailable(product?.display, DISPLAY_LABELS.RAISIN_EXCLUSIVE),
    special_offer_chip: isLabelAvailable(product?.display, DISPLAY_LABELS.SPECIAL_OFFER),
    is_channel_exculsive: isLabelAvailable(product?.display, DISPLAY_LABELS.CHANNEL_EXCLUSIVE),
    is_marketing_leading_rate: isLabelAvailable(
      product?.display,
      DISPLAY_LABELS.MARKET_LEADING_RATE,
    ),
    is_promotional_offer: isLabelAvailable(
      product?.display,
      DISPLAY_LABELS.RAISIN_BANK_PROMOTIONAL_FLAG,
    ),
    is_structured_deposit: isLabelAvailable(product?.display, DISPLAY_LABELS.STRUCTURED_DEPOSIT),
    is_rate_reducing_soon:
      Number(product?.interest_rates?.upcoming_rates[0]?.interest_rate_effective) <
      Number(product?.interest_rates?.interest_rate_effective),
    statutory_deposit_guarantee_scheme: {
      country: {
        id: product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country?.id
          ? product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country?.id
          : 'N/A',
        name: product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country?.name
          ? product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country?.name
          : 'N/A',
        description: product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country
          ?.description
          ? product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country?.description
          : 'N/A',
        rating: product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country.rating
          ? product?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country.rating
          : 'N/A',
      },
    },
    dbs_interest_rates: product?.interest_rates,
    interest_rate_modality: product?.interest_rate_modality,
    pre_contractual_documents: product?.pre_contractual_documents,
  };
};

export const mapBipsToObsProductOffersForDeposits = (
  bipsProducts,
  formatNumber: IntlShape['formatNumber'],
  t,
  formatDate: IntlShape['formatDate'],
  locale: IntlShape['locale'],
): Deposit[] => {
  const depositsMap = new Map<string, Deposit>();

  bipsProducts.entries.forEach((product) => {
    const depositId = product?.id;

    if (!!depositId && !depositsMap.has(depositId)) {
      depositsMap.set(
        depositId,
        mapBipsToObsDeposits(product, formatNumber, t, formatDate, locale),
      );
    }
  });

  return Array.from(depositsMap.values());
};
